import React, { useEffect, useState } from 'react';
import { WithQueryWidgetProps } from './withQueryWidget';
import { useDashboard } from 'routes/dashboard/Dashboard';
import QueryWidgetContentContainer from './QueryWidgetContentContainer';
import QueryWidgetRequestWrapper from './QueryWidgetRequestWrapper';
import WidgetLoader from '../WidgetLoader';
import { useTemplateQueries } from 'routes/dashboard/DashboardPageRoot';
import { Query } from 'system/query/Query';
import APIRequest, { enqueueAPIErrorSnackbar } from 'system/APIRequest';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { QueryInfo } from 'system/query/QueryInfo';
import { permissionTest } from 'system/InfegyUser';
import { Box } from '@mui/system';
import { Alert, Button } from '@mui/material';

export default function QueryWidgetCacheWrapper(props: WithQueryWidgetProps): JSX.Element {
  const dashboard = useDashboard();
  const templateQueriesInfo = useTemplateQueries();
  const { i18n } = useTranslation();

  const { widget } = props;

  const [widgetQueries, setWidgetQueries] = React.useState<Query[]>(props.widget.cache?.cachedQueries || []);
  const [useCache, setUseCache] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [missingQueryError, setMissingQueryError] = useState<boolean>(false);
  const [queryInfo, setQueryInfo] = useState<QueryInfo | undefined>(undefined);

  function getQueryUpdatedDate(): Date | null {
    const hasWidgetQueries = widget.request_info?.queries && widget.request_info.queries.length > 0;
    const hasTemplateQueries = templateQueriesInfo.rawTemplateQueries && templateQueriesInfo.rawTemplateQueries.length > 0;

    // set the earliest date to 0 epoch time
    const queryTimes: Date[] = [];

    // This widget has no queries, so there is nothing to do
    // It shouldn't be possible to get to this state due to the withQueryWidget logic
    if (!hasWidgetQueries && !hasTemplateQueries) {
      return null;
    }

    // If the widget has queries, but they haven't loaded yet, return for now
    if (hasWidgetQueries && !widgetQueries) {
      return null;
    }

    if (widgetQueries && widgetQueries.length > 0) {
      queryTimes.push(...widgetQueries.map(q => {
        if (!q.updated_at) {
          return new Date(0); // If no updated_at, use epoch time
        }
        if (typeof q.updated_at === 'string') {
          return new Date(q.updated_at);
        }
        return q.updated_at;
      }).filter(q => q instanceof Date && !isNaN(q.getTime())));
    }

    if (hasTemplateQueries) {
      queryTimes.push(...templateQueriesInfo.rawTemplateQueries.map(q => {
        if (!q.updated_at) {
          return new Date(0); // If no updated_at, use epoch time
        }
        if (typeof q.updated_at === 'string') {
          return new Date(q.updated_at);
        }
        return q.updated_at;
      }).filter(q => q instanceof Date && !isNaN(q.getTime())));
    }

    // return the most recent query time
    if (queryTimes.length > 0) {
      return new Date(Math.max(...queryTimes.map(q => q.getTime())));
    }

    return null;
  }

  function getUseCache(): boolean {
    // If we are in a template, never use the cache
    if (dashboard.template_type) {
      return false;
    }

    // If we are on a public dashboard, or the user can't update the dashboard,
    // we should always use the cache for now (even if it does not exist),
    // since public dashboards cannot make requests to the API and users
    // without edit permissionts cannot update the widget.
    // This will likely change once public dashboard refresh features are implemented.
    if (dashboard._is_public || !dashboard._modifiable) {
      return true;
    }

    // Ensure the contents of the cache are valid
    let cachedRequests = widget.cache?.cachedRequests;
    if (widget.cache?.cachedRequests && widget.cache?.cachedRequests.length) {
      cachedRequests = widget.cache?.cachedRequests.filter((cachedRequest) => {
        if (Array.isArray(cachedRequest) && cachedRequest.length === 0) {
          return false;
        }
        return !!cachedRequest;
      });
    }
    let cacheItems = widget.cache?.cacheItems;
    if (widget.cache?.cacheItems && widget.cache?.cacheItems.length) {
      cacheItems = widget.cache?.cacheItems.filter((cacheItem) => {
        if (Array.isArray(cacheItem) && cacheItem.length === 0) {
          return false;
        }
        return !!cacheItem;
      });
    }

    // If the widget has no cache, we need to fetch data
    if (!widget.cache?.cachedAt || !cacheItems || !cachedRequests ||
      cachedRequests.length === 0 || cacheItems.length === 0) {
      return false;
    }

    // Validate that the cache hasn't expired based on dashboard update time
    const autoRefreshInterval = dashboard.data?.autoRefreshInterval;
    const now = new Date();
    const cacheTime = new Date(widget.cache.cachedAt);
    // Add a bit of easing, there were issues that the dashboard will refresh
    // quickly enough that the cache is still seen as valid at the time of loading
    const cacheEasing = 10 * 1000; // 10 seconds in milliseconds
    if (autoRefreshInterval && now.getTime() - cacheTime.getTime() >= (autoRefreshInterval * 1000) - cacheEasing) {
      return false;
    }

    // Validate that the cache isn't stale based on dashboard cache invalidation time
    if (dashboard.data?.dashboardCacheInvalidatedAt) {
      const cacheInvalidationTime = new Date(dashboard.data.dashboardCacheInvalidatedAt);
      if (cacheInvalidationTime.getTime() > cacheTime.getTime()) {
        return false;
      }
    }

    // Validate that the cache isn't stale based on query update times
    const mostRecentQueryUpdatedTime = getQueryUpdatedDate();
    if (widget.cache.cachedAt && mostRecentQueryUpdatedTime) {
      const cacheTime = new Date(widget.cache.cachedAt);
      if (mostRecentQueryUpdatedTime.getTime() > cacheTime.getTime()) {
        return false;
      }
    }

    return true;
  }

  async function loadQueries(queryIds: Array<string>): Promise<Array<Query>> {
    let queries: Array<Query> = [];
    
    const response: any = await APIRequest(`obj/query/${queryIds.join(",")}`);

    if (!response.output || !Array.isArray(response.output)) {
      response.output = response?.output ? [response?.output] : [];
    }

    queries = response.output.map((query: any): Query => {
      return new Query(query);
    });

    // Sort the queries by their order in the URL.
    queries.sort((a, b) => {
      // These things should not be possible due to earlier checks, but this shuts up TypeScript.
      if (!queryIds || !a.id || !b.id) {
        return 0;
      }
      return queryIds.indexOf(a.id) - queryIds.indexOf(b.id);
    });

    return queries;
  }

  async function loadWidgetQueries(): Promise<Array<Query>> {
    let storedQueries = widget.request_info?.queries;
    if (!storedQueries) {
      // Compatibility for old widgets that may have queries defined differently
      if (widget.request_info?.query_id) {
        storedQueries = [{ id: widget.request_info.query_id, name: "unknown" }];
      }
    }

    const hasWidgetQueries = storedQueries && storedQueries.length > 0;

    if (!hasWidgetQueries) {
      return Promise.resolve([]);
    }

    // If the widget's queries are different than what we have stored, we need to reload them
    const queryIds = storedQueries?.map(q => q.id).filter(id => id) || [];
    const currentQueryIds = widgetQueries?.map(q => q.id).filter(id => id) || [];
    const queriesAreDifferent = queryIds.length !== currentQueryIds.length ||
      queryIds.some(id => !currentQueryIds.includes(id));

    if (!queriesAreDifferent || queryIds.length === 0) {
      // The queries are the same, nothing to do
      return Promise.resolve(widgetQueries);;
    }

    return await loadQueries(queryIds);
  };

  async function loadQueryInfo(widgetQueries: Array<Query>): Promise<QueryInfo | undefined> {
    let queryInfo: QueryInfo | undefined = undefined;

    // skip loading queryInfo if the user does not have permission to run queries
    if (!permissionTest("run_query")) {
      return queryInfo;
    }

    let datasetId = undefined;

    // Fetch relevant queryInfo to use for settings and validation
    if (widgetQueries && widgetQueries.length) {
      datasetId = widgetQueries[0].dataset_id;
    } else if (templateQueriesInfo.selectedTemplateQueries && templateQueriesInfo.selectedTemplateQueries.length) {
      datasetId = templateQueriesInfo.selectedTemplateQueries[0].query.dataset_id;
    } else if (templateQueriesInfo.rawTemplateQueries && templateQueriesInfo.rawTemplateQueries.length) {
      datasetId = templateQueriesInfo.rawTemplateQueries[0].dataset_id;
    }

    if (!datasetId) {
      return queryInfo;
    }

    const response = await APIRequest("query/info", {}, {
      method: 'POST',
      body: JSON.stringify({
        "dataset_id": datasetId
      }),
    });
    queryInfo = new QueryInfo(response);

    return queryInfo;
  }

  async function load() {
    setIsLoading(true);
    setMissingQueryError(false);

    let useCache = getUseCache();

    let widgetQueries: Array<Query> = [];
    try {
      widgetQueries = await loadWidgetQueries();
    } catch (err) {
      setMissingQueryError(true);
    }

    let queryInfo = undefined;
    try {
      queryInfo = await loadQueryInfo(widgetQueries);
    } catch (error: any) {
      console.error(`Error loading query info: ${error}`);
      enqueueAPIErrorSnackbar(error, enqueueSnackbar);
    }

    setUseCache(useCache);
    setWidgetQueries(widgetQueries);
    setQueryInfo(queryInfo);
    setIsLoading(false);
  }

  useEffect(() => {
    load();
  }, [widget]);

  return (
    <>
      {isLoading &&
        <WidgetLoader />
      }
      {(!isLoading && !missingQueryError) && (
        <>
          {/* If we are using the cache, we can skip straight to rendering the widget content */}
          {useCache === true && (
            <QueryWidgetContentContainer
              {...props}
              queryInfo={queryInfo}
              widgetQueries={widgetQueries}
              activeWidgetRequests={widget.cache?.cachedRequests || []}
              apiResponses={widget.cache?.cacheItems || []}
              >
            </QueryWidgetContentContainer>
          )}
          {useCache === false && (
            <QueryWidgetRequestWrapper
              queryInfo={queryInfo}
              widgetQueries={widgetQueries}
              {...props}
            >
            </QueryWidgetRequestWrapper>
          )}
        </>
      )}
      {missingQueryError && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">
            {i18n.t("widgets.errors.query-not-found-warning")}
            <Button variant="contained" color="primary" sx={{ ml: 0 }} size="small" onClick={() => {
              // Remove the missing queries from the widget
              props.saveWidgetQueries([]).then(() => {
                setMissingQueryError(false);
              });
            }}>
              {i18n.t("widgets.errors.remove-widget-queries")}
            </Button>
          </Alert>
        </Box>
      )}
    </>
  );
}
